/* eslint-disable react/no-unused-prop-types */
import { connect } from 'react-redux';
import cn from 'classnames';
import { useState } from 'react';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getActEventStudiedDocumentsBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmErknmEventSubmittedDocumentsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmEventSubmittedDocumentsForm';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import { ErknmActEventItemBodyProtocolsStudiedDocumentsDocument } from './ErknmActEventItemBodyProtocolsStudiedDocumentsDocument';
import styles from './ErknmActEventItemBodyProtocolsStudiedDocuments.module.scss';
import { lodashGet } from '../../../../../../../functions/lodashGet';
import { convertQuillDeltaToHtml } from '../../../../../../../functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from '../../../../../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { checkHasErrorInCollapseBlock } from '../../../../../../../functions/checkHasErrorInCollapseBlock';
import { Row } from '../../../../../../../UIPackage/FormModule/components/Row';
import { DatePickerField } from '../../../../../../../UIPackage/MainModule/components/DatePickerField';

interface IErknmActEventItemBodyProtocolsStudiedDocumentsProps {
  eventId: string;
  name: string;
  studiedDocumentsValue: IKnmErknmEventSubmittedDocumentsForm[];
  isDecision?: boolean;
}

interface IErknmActEventItemBodyProtocolsStudiedDocumentsStoreProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

const ErknmActEventItemBodyProtocolsStudiedDocumentsWithoutStore = ({
  eventId,
  name,
  studiedDocumentsValue,
  formRef,
}: IErknmActEventItemBodyProtocolsStudiedDocumentsProps &
  IErknmActEventItemBodyProtocolsStudiedDocumentsStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const [shouldResize, setShouldResize] = useState<boolean>(false);

  const renderBody = () => {
    return studiedDocumentsValue.map((studiedDocument, sIndex) => {
      const studiedDocumentFieldName = `${name}[${sIndex}]`;
      const id = getActEventStudiedDocumentsBlockId(eventId, studiedDocument.guid || '');

      const quillDeltaValue = lodashGet(
        formRef?.current?.getState().values,
        `${studiedDocumentFieldName}.descriptionReactQuillEditor`,
      );

      const hasError = checkHasErrorInCollapseBlock(formRef, studiedDocumentFieldName);

      return (
        <KnmCollapse
          id={id}
          theme="fields"
          title={studiedDocument.documentTitle || ''}
          key={id}
          onToggleCallback={() => setShouldResize(!shouldResize)}
          headerPostfix={
            <div
              className={cn(styles.StatusSubmittedDocument, {
                [styles.Blue]: studiedDocument.status?.code === 'SD-1',
                [styles.Red]: studiedDocument.status?.code === 'SD-2',
                [styles.Green]: studiedDocument.status?.code === 'SD-4',
              })}
            >
              {studiedDocument.status?.title}
            </div>
          }
          changeOpenedCallback={e =>
            setKnmCollapseOpened(state => ({
              ...state,
              [studiedDocumentFieldName]: e,
            }))
          }
          error={hasError && knmCollapseOpened[studiedDocumentFieldName]}
        >
          <>
            {studiedDocument.status?.code !== 'SD-4' &&
              studiedDocument.status?.code !== 'SD-2' &&
              studiedDocument.files && (
                <ErknmActEventItemBodyProtocolsStudiedDocumentsDocument
                  name={`${studiedDocumentFieldName}.files`}
                  files={studiedDocument.files}
                />
              )}
            <FieldBlock
              id={`${studiedDocumentFieldName}.description`}
              label={'Описание'}
              margin="bottom"
              placementLabel="left"
              color="black"
              labelSize="col-3"
            >
              {quillDeltaValue && typeof quillDeltaValue === 'object' ? (
                <div
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{
                    __html: convertQuillDeltaToHtml(quillDeltaValue as IReactQuillDelta),
                  }}
                  className="ql-editor ql-editor-preview-block"
                  style={{ whiteSpace: 'pre-wrap', width: '100%' }}
                />
              ) : (
                <>{quillDeltaValue}</>
              )}
            </FieldBlock>
            <Row>
              <FieldBlock
                label="Дата и время запроса"
                color="black"
                size="half"
                placementLabel="left"
                id={`${studiedDocumentFieldName}.requestDateTime`}
              >
                <DatePickerField
                  id={`${studiedDocumentFieldName}.requestDateTime`}
                  name={`${studiedDocumentFieldName}.requestDateTime`}
                  disabled={true}
                  timeFormat
                />
              </FieldBlock>
              <FieldBlock
                label="Срок представления"
                color="black"
                size="half"
                placementLabel="left"
                id={`${studiedDocumentFieldName}.submissionDeadline`}
              >
                <DatePickerField
                  id={`${studiedDocumentFieldName}.submissionDeadline`}
                  name={`${studiedDocumentFieldName}.submissionDeadline`}
                  disabled={true}
                />
              </FieldBlock>
            </Row>

            <Row>
              <FieldBlock
                label="Дата и время получения документа"
                color="black"
                size="half"
                placementLabel="left"
                id={`${studiedDocumentFieldName}.submissionDateTime`}
              >
                <DatePickerField
                  id={`${studiedDocumentFieldName}.submissionDateTime`}
                  name={`${studiedDocumentFieldName}.submissionDateTime`}
                  disabled={true}
                  timeFormat
                />
              </FieldBlock>
            </Row>
          </>
        </KnmCollapse>
      );
    });
  };

  return (
    <FieldBlock label={'Изученные документы'} labelId={`${eventId}.studiedDocuments`} margin="top">
      <div className={styles.HelpText}>
        <Hint text='Документы загружаются в разделе "Истребованные документы"' />
      </div>
      {renderBody()}
    </FieldBlock>
  );
};

const mapStateToProps = (
  state: IRootState,
  ownProps: IErknmActEventItemBodyProtocolsStudiedDocumentsProps,
): IErknmActEventItemBodyProtocolsStudiedDocumentsStoreProps => ({
  formRef: ownProps?.isDecision ? getDecisionFormRef(state) : getKnmFormRef(state),
});

export const ErknmActEventItemBodyProtocolsStudiedDocuments = connect(mapStateToProps)(
  ErknmActEventItemBodyProtocolsStudiedDocumentsWithoutStore,
);
